import type { Experiments, ILogger } from '@wix/thunderbolt-symbols'
import { viewer_components_id_prefixes } from './componentUtils'

export interface DualModeSelectorOptions {
	experiments?: Experiments
	document?: Document
	componentIds?: Set<string>
	logger?: ILogger
}

export function getShouldUseClassSelector(options?: DualModeSelectorOptions): boolean {
	let experiments = options?.experiments

	// Fallback to window.viewerModel.experiments if not provided
	if (!experiments && typeof window !== 'undefined') {
		try {
			const windowAny = window as any
			experiments = windowAny.viewerModel?.experiments
		} catch (error) {
			// Silent fail
		}
	}

	if (!experiments) {
		return false
	}

	const useClassSelectors = experiments['specs.thunderbolt.useClassSelectorsForLookup']
	const addIdAsClassName = experiments['specs.thunderbolt.addIdAsClassName']
	return !!(useClassSelectors && addIdAsClassName)
}

export function getDocument(options: DualModeSelectorOptions): Document | null {
	return options.document || (typeof document !== 'undefined' ? document : null)
}

/**
 * Converts an ID-based selector to a class-based selector.
 * Handles various selector patterns:
 * - #id → .id
 * - [id="value"] → [class~="value"]
 * - [id^="value"] → [class^="value"]
 * - [id*="value"] → [class*="value"]
 * - [id$="value"] → [class$="value"]
 *
 * @param selector - The ID-based selector to convert
 * @returns The class-based selector
 */
export function convertIdSelectorToClassSelector(selector: string): string {
	return (
		selector
			// Replace #id with .id
			.replace(/#([a-zA-Z0-9_-]+)/g, '.$1')
			// Replace [id="value"] with [class~="value"] (exact match in space-separated list)
			.replace(/\[id="([^"]+)"\]/g, '[class~="$1"]')
			// Replace [id^="value"] with [class^="value"] (starts with)
			.replace(/\[id\^="([^"]+)"\]/g, '[class^="$1"]')
			// Replace [id*="value"] with [class*="value"] (contains)
			.replace(/\[id\*="([^"]+)"\]/g, '[class*="$1"]')
			// Replace [id$="value"] with [class$="value"] (ends with)
			.replace(/\[id\$="([^"]+)"\]/g, '[class$="$1"]')
	)
}

function logFallbackToId(logger: ILogger | undefined, compId: string, selectorType: string) {
	// Track fallback in BI to identify components missing className - only in production
	if (logger && process.env.NODE_ENV !== 'development') {
		logger.meter('dom_selector_id_fallback', {
			customParams: {
				compId,
				selectorType,
			},
		})
	}

	// Also log to console in development for immediate debugging or if no logger is provided
	if (!logger || process.env.NODE_ENV === 'development') {
		console.warn("[DOM Selectors] Fallback to ID for '" + compId + "' (" + selectorType + ').')
	}
}

export function getByCompId(compId: string, options: DualModeSelectorOptions = {}): HTMLElement | null {
	const doc = getDocument(options)
	if (!doc) return null

	if (!compId || typeof compId !== 'string') {
		return null
	}

	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const element = doc.querySelector(`.${compId}`) as HTMLElement | null
		if (element) {
			return element
		}
	}

	const element = doc.getElementById(compId)
	if (element && shouldUseClassSelector) {
		logFallbackToId(options.logger, compId, 'getElementById')
	}
	return element
}

/**
 * Generic querySelector that automatically converts ID-based selectors to class-based selectors
 * when the experiment is enabled. Falls back to ID-based selectors if no elements are found.
 *
 * @param selector - The selector string (can include #id, [id^="..."], etc.)
 * @param options - Dual mode selector options
 * @returns The first matching element or null
 */
export function querySelector(selector: string, options: DualModeSelectorOptions = {}): Element | null {
	const doc = getDocument(options)
	if (!doc) return null

	if (!selector || typeof selector !== 'string') return null

	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const classSelector = convertIdSelectorToClassSelector(selector)
		const element = doc.querySelector(classSelector)
		if (element) {
			return element
		}
	}

	const element = doc.querySelector(selector)
	if (element && shouldUseClassSelector) {
		logFallbackToId(options.logger, selector, 'querySelector')
	}

	return element
}

/**
 * Generic querySelectorAll that automatically converts ID-based selectors to class-based selectors
 * when the experiment is enabled. Falls back to ID-based selectors if no elements are found.
 *
 * @param selector - The selector string (can include #id, [id^="..."], etc.)
 * @param options - Dual mode selector options
 * @returns Array of matching elements
 */
export function querySelectorAll(selector: string, options: DualModeSelectorOptions = {}): Element[] {
	const doc = getDocument(options)

	if (!doc) return []
	if (!selector || typeof selector !== 'string') return []

	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const classSelector = convertIdSelectorToClassSelector(selector)
		const elements = Array.from(doc.querySelectorAll(classSelector))
		if (elements.length > 0) {
			return elements
		}
	}

	const elements = Array.from(doc.querySelectorAll(selector))
	if (elements.length > 0 && shouldUseClassSelector) {
		logFallbackToId(options.logger, selector, 'querySelectorAll')
	}

	return elements
}

export function getClosestByCompId(
	element: Element,
	compId: string,
	options: DualModeSelectorOptions = {}
): Element | null {
	if (!compId || typeof compId !== 'string') {
		return null
	}

	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const closestElement = element.closest(`.${compId}`)
		if (closestElement) {
			return closestElement
		}
	}

	const closestElement = element.closest(`#${compId}`)
	if (closestElement && shouldUseClassSelector) {
		logFallbackToId(options.logger, compId, 'getClosestByCompId')
	}

	return closestElement
}

export function getElementCompId(element: Element, options: DualModeSelectorOptions = {}): string {
	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const classes = Array.from(element.classList || [])

		if (options.componentIds) {
			const compId = classes.find((cls) => options.componentIds!.has(cls))
			if (compId) {
				return compId
			}
		}

		// Fallback: Look for component ID pattern (comp-*)
		const compId = classes.find((cls) => viewer_components_id_prefixes.some((prefix) => cls.startsWith(prefix)))
		if (compId) {
			return compId
		}
	}

	const elementId = element.id
	if (elementId && shouldUseClassSelector) {
		logFallbackToId(options.logger, elementId, 'getElementCompId')
	}

	return elementId
}
